<template>
    <div class="tile-inner-grid gap-2">
        <div
            class="grid-area-image mx--2 mt--2"
            :class="hasImage ? '' : 'backdrop-light'"
            :style="{
                'position': 'relative',
                'background-image': hasImage ? 'url(' + (event.images[0].urlSm || event.images[0].url) + ')' : '',
                'background-position': hasImage && event.images[0].imageCenter ? event.images[0].imageCenter : 'center center',
                'background-size': 'cover'
            }">
            <ImageLicence
                v-if="hasImage && event.images[0].licence"
                :licence="event.images[0].licence" />
        </div>
        <div class="grid-area-date">
            <div class="h-100">
                <div
                    v-if="showPrices || event.startDate || event.endDate"
                    class="d-flex flex-wrap justify-content-center align-items-center h-100">
                    <div v-if="event.startDate && event.endDate">
                        <small
                            v-if="!$moment(event.startDate).isSame(event.endDate, 'day')"
                            class="font-weight-600">
                            <span class="text-nowrap">
                                <span v-if="$moment(event.startDate).isSame(event.endDate, 'month')">
                                    {{ $df($moment(event.startDate), 'dateNumericDay') }}.
                                </span>
                                <span v-else>
                                    <span v-if="$moment(event.startDate).isSame(event.endDate, 'year')">
                                        {{ $df($moment(event.startDate), 'dateShortMonth') }}
                                    </span>
                                    <span v-else>
                                        {{ $df($moment(event.startDate), 'dateShort') }}
                                    </span>
                                </span>
                                <span>-</span>
                                <span>
                                    {{ $df($moment(event.endDate), 'dateShort') }}
                                </span>
                            </span>
                        </small>
                        <span v-else>
                            <small class="text-nowrap font-weight-600">
                                <span v-if="!$moment(event.startDate).isSame($moment(event.startDate).startOf('day'))">
                                    {{ $df($moment(event.startDate), 'dateTimeShort') }}
                                </span>
                                <span v-else>
                                    {{ $df($moment(event.startDate), 'dateShort') }}
                                </span>
                            </small>
                        </span>
                    </div>
                    <div
                        v-if="event.icalContent || showPrices"
                        class="ml-2 small d-flex align-items-center fab gap-2">
                        <div v-if="showPrices">
                            <el-popover trigger="hover">
                                <template #reference>
                                    <span class="d-flex gap-2 text-theme-heading">
                                        <font-awesome-icon icon="coins" />
                                    </span>
                                </template>
                                <div class="text-center">
                                    <h3
                                        v-if="!event.price && !event.price2"
                                        class="mb-0 text-theme-heading">
                                        {{ $t('iFrame.chargeable') }}
                                    </h3>
                                    <h3
                                        v-else
                                        class="font-weight-bold mb-0 text-theme-heading">
                                        {{ event.price || '-' }}
                                    </h3>
                                    <small
                                        v-if="event.price2"
                                        class="mb-0 text-theme-heading">{{
                                            event.price2
                                        }}</small>
                                </div>
                            </el-popover>
                        </div>
                        <EventEntryIcalElement
                            v-if="event.icalContent"
                            :event="event"
                            no-label />
                    </div>
                </div>
            </div>
        </div>
        <div class="grid-area-title d-flex justify-content-center align-items-start">
            <div class="text-center">
                <h3 class="font-weight-bolder">
                    {{ event.name }}
                </h3>
                <div>
                    <small v-if="event.ageFrom">{{ $t('iFrame.from') }}</small>
                    <small v-if="event.ageFrom">{{ event.ageFrom }}</small>
                    <small v-if="event.ageTo">{{ $t('iFrame.to') }}</small>
                    <small v-if="event.ageTo">{{ event.ageTo }}</small>
                    <small v-if="event.ageFrom || event.ageTo">{{ $t('iFrame.years') }}</small>
                </div>
                <EventCategories
                    v-if="!hideCategories && event.categories"
                    class="justify-content-center"
                    smaller
                    :max="3"
                    :categories="event.categories" />
            </div>
        </div>
        <div class="grid-area-location d-flex justify-content-center align-items-start">
            <EventEntryLocationAndOrganizerInfo
                :event="event"
                smaller />
        </div>
        <div class="grid-area-action d-flex justify-content-center align-items-end">
            <div v-if="event.registrationLink && !registrationDeactivated">
                <EventEntryRegistrationButton :event="event" />
            </div>
            <small v-else-if="event.registrationLink && registrationPlanned">
                {{
                    $t('iFrame.registrationPlannedShort', {registrationStart: $df($moment(event.registrationStart), 'dateNumeric')})
                }}
            </small>
        </div>
    </div>
</template>

<script>
import EventCategories from "@/components/pages/eventIFrame/EventEntry/EventEntryOverview/EventCategories";
import EventEntryLocationAndOrganizerInfo
    from "@/components/pages/eventIFrame/EventEntry/EventEntryOverview/EventEntryLocationAndOrganizerInfo";
import EventEntryIcalElement from "@/components/pages/eventIFrame/EventEntry/EventEntryOverview/EventEntryIcalElement";
import EventEntryRegistrationButton
    from "@/components/pages/eventIFrame/EventEntry/EventEntryOverview/EventEntryRegistrationButton";

export default {
    name: "EventOverviewForTileDisplay",
    components: {
        EventEntryRegistrationButton,
        EventEntryIcalElement,
        EventEntryLocationAndOrganizerInfo,
        EventCategories
    },
    props: {
        event: {
            type: Object,
            required: true
        },
        hideCategories: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        showTimes() {
            return this.dateTimeHasTime(this.event.startDate) || this.dateTimeHasTime(this.event.endDate)
        },
        showPrices() {
            return this.event?.price || this.event?.price2 || this.event?.chargeable
        },
        registrationDeactivated() {
            if (this.event.registrationDeactivated) return true
            if (this.event.registrationStart && this.$moment().isSameOrBefore(this.$moment(this.event.registrationStart)))
                return true

            if (this.event.registrationEnd) {
                if (this.$moment(this.event.registrationEnd).isSame(this.$moment(this.event.registrationEnd).startOf('day'), 'second'))
                    return this.$moment(this.event.registrationEnd).endOf('day').isBefore(this.$moment())
                else
                    return this.$moment(this.event.registrationEnd).isBefore(this.$moment())
            }
            return false
        },
        registrationPlanned() {
            if (this.event.registrationDeactivated) return false
            if (this.event.registrationStart && this.$moment().isSameOrBefore(this.$moment(this.event.registrationStart)))
                return true
            return false
        },
        hasImage() {
            return !!this.event?.images?.length
        }
    },
    methods: {
        dateTimeHasTime(dateTime) {
            return !(this.$moment(dateTime).hour() === 0 &&
                this.$moment(dateTime).minute() === 0 &&
                this.$moment(dateTime).second() === 0)
        }
    }
}
</script>

<style lang="scss" scoped>
.tile-inner-grid {
    display: grid;
    gap: map-get($spacers, 2);
    padding-bottom: .5rem;
    height: 100%;
    grid-template-columns: 1fr;
    grid-template-rows: 125px 1.5rem auto 1fr 3rem;
    grid-template-areas:
        "image"
        "date"
        "title"
        "price"
        "action";
}

.grid-area-image {
    grid-area: image;
}

.grid-area-date {
    grid-area: date;
    position: relative;
}

.grid-area-title {
    grid-area: title;
}

.grid-area-price {
    grid-area: price;
    min-height: 90px;
}

.grid-area-action {
    grid-area: action;
}

.backdrop-light {
    background-color: rgba($gray-600, 0.2);
    background-blend-mode: multiply;
}

.fab {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
}

</style>
